import React, { useState, useMemo } from 'react';
import {
  arrayOf, string, exact, number, func, bool,
} from 'prop-types';
import styled, { css } from 'styled-components';
import { Icon, Flex, Typography } from '@atoms';
import { ReactComponent as PlusIcon } from '@assets/plus.svg';
import { ReactComponent as ArrowIcon } from '@assets/arrow-down.svg';
import { getDiscountValue } from '@helpers/common';

const EntitySpecials = ({
  specials, onSpecialAdd, onSpecialEdit,
}) => {
  const [isListExpanded, setIsListExpanded] = useState(false);
  const handleExpandedChangeState = () => {
    setIsListExpanded(prevState => !prevState);
  };

  const specialsList = useMemo(() => (
    specials.map(({
      id, sizeName, discountType, discountValue,
    }) => (
      <SpecialsListItem
        marginTop="lg"
        paddingBottom="lg"
        key={id}
        onClick={onSpecialEdit(id)}
        row
        spaceBetween
        itemsCenter
      >
        <SizedFlex itemsCenter width="75%">
          <RedDot grow={0} shrink={0} marginRight="lg" marginLeft="sm" />
          <SizeName size="lg">{sizeName}</SizeName>
        </SizedFlex>
        <SizedFlex width="25%" row justifyEnd>
          <Typography size="lg">{getDiscountValue(discountType, discountValue)}</Typography>
        </SizedFlex>
      </SpecialsListItem>
    ))
  ), [specials]);

  return (
    <SizedFlex width="100%" column justifyStart itemsCenter paddingLeft="md" paddingRight="md">
      <SpecialsListItem
        marginTop="lg"
        paddingBottom="lg"
        paddingRight="sm"
        row
        spaceBetween
        itemsCenter
      >
        <ConstrainedSizeTextWrapper
          width="90%"
          column
          justifyStart
          itemsStart
          shrink={0}
        >
          <Typography size="xl" weight="medium">Specials</Typography>
        </ConstrainedSizeTextWrapper>
        <SizedFlex onClick={handleExpandedChangeState} width="10%" row justifyEnd itemsStart shrink={0}>
          <StyledArrow SVG={ArrowIcon} width="20px" height="20px" isExpanded={isListExpanded} />
        </SizedFlex>
      </SpecialsListItem>
      {isListExpanded && (
        <SizedFlex width="100%" column justifyStart itemsStart paddingLeft="lg">
          <SpecialsListItem
            onClick={onSpecialAdd}
            marginTop="lg"
            paddingBottom="lg"
            row
            justifyStart
            itemsCenter
          >
            <IconContainer grow={0} marginRight="xl4" padding="xxs">
              <Icon SVG={PlusIcon} width="15px" height="15px" />
            </IconContainer>
            <Typography size="lg" weight="medium">
              Add New Special
            </Typography>
          </SpecialsListItem>
          {specialsList}
        </SizedFlex>
      )}
    </SizedFlex>
  );
};

const SizedFlex = styled(Flex)`
  ${({ width }) => css`
    width: ${width};
    max-width: ${width};
  `}
`;

const SizeName = styled(Typography)`
  text-align: left;
`;

const SpecialsListItem = styled(Flex)`
  ${({ theme: { color } }) => css`
    width: 100%;
    border-bottom: 1px solid ${color.warmGrey};
  `}
`;

const IconContainer = styled(Flex)`
  ${({ theme: { border, color } }) => css`
    border-radius: ${border.radius.circular};
    background-color: ${color.grannySmith};
  `}
`;

const ConstrainedSizeTextWrapper = styled(SizedFlex)`
  ${({ width }) => css`
    ${Typography} {
      max-width: ${width};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `}
`;

const StyledArrow = styled(Icon)`
  ${({ isExpanded }) => css`
    ${isExpanded && 'transform: rotate(180deg);'}
    transition: transform 0.5s;
  `}
`;

const RedDot = styled(Flex)`
  ${({ theme: { border, color, spacing } }) => css`
    width: ${spacing.sm};
    height: ${spacing.sm};
    border-radius: ${border.radius.circular};
    background-color: ${color.strawberry};
  `}
`;

EntitySpecials.propTypes = {
  onSpecialEdit: func.isRequired,
  onSpecialAdd: func.isRequired,
  specials: arrayOf(exact({
    id: number,
    categoryId: number,
    available: bool,
    availabilityIds: arrayOf(number),
    sizeId: number,
    sizeName: string,
    discountType: string,
    discountValue: number,
  })).isRequired,
};

export default EntitySpecials;
